import React from 'react';
import { MealCard, PlatformIcon } from '@cubemeet/ui';
import { View, TouchableOpacity } from 'react-native';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { coalesceText, mergePayload, getTypeIcon, ORANGE, buildMetaPairs, arrayify } from './itineraryUtils';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
}

const PoiAdapter: React.FC<Props> = ({ it, isExpanded = false, onOpenPoi, dayDate, onImageClick }) => {
  const mergedPayload = mergePayload(it) || {};
  const title = coalesceText(it?.title, it?.card?.title, mergedPayload.title) || '未命名';
  // 優先使用 it.note（行程表副本的描述），再使用 it.description（前端更新後的），最後才使用 card.description（原始卡片）
  const description = coalesceText(
    it?.note,
    it?.description,
    mergedPayload.description,
    it?.card?.description,
    mergedPayload.summary,
    mergedPayload.intro,
  );

  // Only show image from external card (cards table) when that card provides a cover image/gallery.
  const hasCardObj = Boolean(it?.card && Object.keys(it.card || {}).length > 0) || Boolean(it?.card_id || it?.cardId || (it?.meta && (it.meta.cardId || it.meta.card_id)));
  const hasCardCover = Boolean(it?.card && (it.card.cover_image || (Array.isArray(it.card.image_gallery) && it.card.image_gallery.length > 0)));
  const imageUrl = hasCardCover ? (it?.card?.cover_image || mergedPayload.cover_image ||
    (Array.isArray(it?.card?.image_gallery) && it.card.image_gallery.length ? it.card.image_gallery[0] : undefined) ||
    (Array.isArray(mergedPayload?.image_gallery) && mergedPayload.image_gallery.length ? mergedPayload.image_gallery[0] : undefined) ||
    undefined) : undefined;

  // If imageUrl is the default placeholder, treat it as undefined so we can show the map instead
  const isPlaceholder = imageUrl && (imageUrl.includes('card-header-default.jpg') || imageUrl.includes('placeholders/card-header-default'));
  const effectiveImageUrl = isPlaceholder ? undefined : imageUrl;

  const lat = typeof mergedPayload.lat === 'number' ? mergedPayload.lat : 
              (typeof mergedPayload.latitude === 'number' ? mergedPayload.latitude : 
              (typeof it?.card?.latitude === 'number' ? it.card.latitude : 
              (typeof it?.card?.lat === 'number' ? it.card.lat : undefined)));
              
  const lng = typeof mergedPayload.lng === 'number' ? mergedPayload.lng : 
              (typeof mergedPayload.longitude === 'number' ? mergedPayload.longitude : 
              (typeof it?.card?.longitude === 'number' ? it.card.longitude : 
              (typeof it?.card?.lng === 'number' ? it.card.lng : undefined)));
  const hasCoordinates = lat !== undefined && lng !== undefined;

  const firstChar = (title || '').charAt(0).toUpperCase();
  const customIcon = L.divIcon({
    className: 'custom-map-marker',
    html: `<div style="
      background-color: #F97316;
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      font-weight: bold;
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.2);
    ">${firstChar}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });

  const mapPreview = (!effectiveImageUrl && hasCoordinates) ? (
    <div style={{ height: '100%', width: '100%', zIndex: 0, position: 'absolute', top: 0, left: 0 }}>
      <MapContainer 
        center={[lat!, lng!]} 
        zoom={15} 
        style={{ height: '100%', width: '100%' }} 
        zoomControl={false} 
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
        attributionControl={false}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[lat!, lng!]} icon={customIcon} />
      </MapContainer>
    </div>
  ) : undefined;

  // Helper: safely extract a readable string from a value that may be a string, object, or array.
  const extractString = (value: any): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
      const parts = value.map(v => extractString(v)).filter(Boolean) as string[];
      return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
      // common address/name fields to try
      const candidates = [
        'formatted_address', 'formattedAddress', 'address', 'display', 'label', 'name', 'title', 'short_name', 'description', 'locality'
      ];
      for (const k of candidates) {
        if (value[k]) {
          const s = extractString(value[k]);
          if (s) return s;
        }
      }
      // fallback: try to stringify meaningful primitive props
      const values = Object.values(value).map(v => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''))).filter(Boolean);
      if (values.length) return values.join(' ');
      return undefined;
    }
    return String(value).trim() || undefined;
  };

  const normalizeForCompare = (s?: string): string => {
    if (!s) return '';
    return s
      .toLowerCase()
      .replace(/[\u2000-\u206F\u2E00-\u2E7F\p{P}\p{S}]/gu, ' ') // strip punctuation/symbols to spaces
      .replace(/\s+/g, ' ')
      .trim();
  };

  let venue = extractString(coalesceText(it?.card?.location, mergedPayload.location, mergedPayload.address));
  // Prioritize mergedPayload.address (includes custom_payload override) over it.card.address
  const address = extractString(mergedPayload.address || it?.card?.address);

  // Clean up description to avoid duplicating title/address when data sources contain combined strings.
  let cleanedDescription: string | undefined = description ? String(description).trim() : undefined;
  try {
    if (cleanedDescription) {
      // remove occurrences of title/address or combinations (robust, case-insensitive)
      const tokens = [title, address, `${title} ${address}`, `${address} ${title}`].filter(Boolean).map(String);
      for (const t of tokens) {
        if (!t) continue;
        const re = new RegExp(t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
        cleanedDescription = cleanedDescription.replace(re, '').trim();
      }
      // also remove leftover repeated whitespace
      cleanedDescription = cleanedDescription.replace(/\s+/g, ' ').trim();
      // if after cleaning the description equals title or address, drop it
      if (!cleanedDescription) cleanedDescription = undefined;
      if (cleanedDescription && (normalizeForCompare(cleanedDescription) === normalizeForCompare(title) || normalizeForCompare(cleanedDescription) === normalizeForCompare(address))) {
        cleanedDescription = undefined;
      }
    }
  } catch (e) {
    // non-fatal: keep original description if cleaning fails
    cleanedDescription = description;
  }
  // 防御性清理：如果上面的替换在开头留下了标点（例如原始为 "地点，备注"，删除地点后会剩下 "，备注"），
  // 那么去掉开头的标点/空白/零宽字符，避免 UI 出现孤立的顿号或其他符号。
  try {
    if (cleanedDescription) {
      cleanedDescription = String(cleanedDescription).replace(/^[\s,，、;；:\u3001\uFEFF\u200B\u00A0]+/, '').trim();
      if (cleanedDescription === '') cleanedDescription = undefined;
    }
  } catch (e) {
    // ignore
  }

  // Also avoid showing venue when it's just a duplicate of title or title+address
  try {
    if (venue) {
      const v = String(venue).trim();
      const normV = normalizeForCompare(v);
      const candidates = [title, address, `${title} ${address}`, `${address} ${title}`].filter(Boolean).map(normalizeForCompare);
      if (candidates.includes(normV)) {
        venue = undefined;
      }
    }
  } catch (e) {
    // ignore
  }
  const phone = typeof (it?.card?.phone || mergedPayload.phone) === 'string' ? (it?.card?.phone || mergedPayload.phone) : undefined;
  const openingHours = it?.card?.opening_hours || mergedPayload.opening_hours || mergedPayload.openingHours;
  const metaPairs = buildMetaPairs(it, mergedPayload);
  const transportNote = (() => {
    const entry = metaPairs.find((pair) => {
      if (!pair || !pair.label) return false;
      const label = String(pair.label).trim();
      return label === '交通方式' || label === '交通';
    });
    if (!entry || !entry.value) return undefined;
    return String(entry.value).trim() || undefined;
  })();
  const tags = Array.isArray(it?.cardTags) ? it.cardTags : (Array.isArray(it?.card?.tags) ? it.card.tags : arrayify(mergedPayload.tags));

  const payloadForModal = {
    title,
    imageUrl: effectiveImageUrl,
    address,
    // Prefer raw itinerary note from DB when available (do not modify it),
    // otherwise fall back to cleanedDescription/description.
    description: (typeof it?.note === 'string' && it.note !== null && it.note !== undefined && it.note !== '') ? it.note : (cleanedDescription || description),
    metaPairs,
    tags,
    rawDebug: { rawMergedPayload: mergedPayload },
    card: it?.card,
    // Ensure id is always available for sharing
    id: it?.card?.id || it?.meta?.cardId || it?.cardId,
    cardId: it?.card?.id || it?.meta?.cardId || it?.cardId,
  };
  
  // Extract image gallery for lightbox
  const imageGallery = it?.card?.image_gallery || [];
  const galleryImages = Array.isArray(imageGallery) ? imageGallery.filter((img: any) => img?.url) : [];
  
  const card = (
    <MealCard
      title={title}
      // Show DB `it.note` raw and unmodified when present; otherwise use cleanedDescription/description
      note={(typeof it?.note === 'string' && it.note !== null && it.note !== undefined && it.note !== '') ? it.note : (cleanedDescription || description)}
      restaurantName={undefined}
      venue={venue}
      cuisine={undefined}
      course={undefined}
      price={undefined}
      currency={undefined}
      startTime={mergedPayload.start_time || mergedPayload.startTime}
      endTime={mergedPayload.end_time || mergedPayload.endTime}
      imageUrl={effectiveImageUrl}
      address={address}
      phone={phone}
      openingHours={openingHours}
      actionIcon={<PlatformIcon name={getTypeIcon('poi')} size={16} color={ORANGE} />}
      transportNote={transportNote}
      dayDate={dayDate}
      onImageClick={galleryImages.length > 0 && onImageClick ? () => onImageClick(galleryImages, 0) : undefined}
      headerContent={mapPreview}
    />
  );

  if (typeof onOpenPoi === 'function') {
    return (
      <TouchableOpacity activeOpacity={0.8} onPress={() => {
        try {
          // debug: print payload so devs can inspect why modal may be empty
          // eslint-disable-next-line no-console
          console.log('[PoiAdapter] openPoi payload:', payloadForModal);
        } catch (e) {}
        // Pass through the full itinerary item so the modal wrapper can read custom_payload.metaPairs
        onOpenPoi({ ...payloadForModal, _item: it });
      }}>
        {card}
      </TouchableOpacity>
    );
  }

  return card;
};

export default PoiAdapter;
